import {HttpClient, HttpHeaders} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {User} from '../models/user.model';
import {BehaviorSubject, forkJoin, Observable, of} from 'rxjs';
import {jwtDecode} from 'jwt-decode';
import {map} from 'rxjs/operators';
import {environment} from '../../environments/environment';
import {Router} from "@angular/router";
import * as aes from 'crypto-js/aes';
import {JwtHelperService} from "@auth0/angular-jwt";
import {Screen} from "../core/guards/RoleEnum.enum";
import {LoaderService} from "./loader.service";
import {RxChain} from "../models/rx-chain";
import { UserTypeEnum } from '../core/guards/UserTypeEnum.enum';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  jwtHelper = new JwtHelperService();
  baseUrl = environment.accountLoginApiUrl;
  private readonly _changeLoggedPass: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private readonly _changePassSuccess: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  currentUserSource = new BehaviorSubject<any>(null);
  private static readonly KEY: string = "hyhheyh5ytdFJYFG==";

  constructor(private readonly http: HttpClient,
    private readonly router: Router,
              private readonly loader: LoaderService) {
    const user = JSON.parse(localStorage.getItem('user') ?? '{}');
    if (user) {
      this.currentUserSource.next(user);
    }
  }

  forgotPassword(email: string): Observable<any> {
    let url: string = `${this.baseUrl}auth/resetPassword`;
    url += `?email=${email}`;
    return this.http.get(url);
  }

  login(email: string, password: string): RxChain<User> {
    this.loader.putMsgLoader('Realizando login...');
    const login$ = this.http.post<User>(this.baseUrl + 'auth/login', {email, password})
      .pipe(
        map((response: User) => {
          if (!response.contracts?.length) {
            throw new Error('Usuário não possui contrato associado');
          }
          response.groups = response.contracts;
          return response;
        })
      );

    return RxChain.start(login$);
  }

  getAllPermissions(userId: any): Observable<any[]> {
    const headers = new HttpHeaders({Authorization: this.getToken()});
    return this.http.get<any[]>(`${this.baseUrl}users/module/profile-modules?userId=${userId}`, {headers})
    // .pipe(
    //   map(arrays => arrays.flat())
    // );
  }

  getPermissions(user: User): Observable<any[]> {
    this.loader.putMsgLoader('Consultando permissões...');
    const requests = user.contracts!.map(c => {
      const headers = new HttpHeaders({Authorization: user.accessToken});
      return this.http.get<any[]>(`${this.baseUrl}auth/acl/modules?userId=${user.id}&contractId=${c.id}`, {headers});
    });
    return forkJoin(requests).pipe(
      map(arrays => arrays.flat())
    );
  }

  getLegacyId(user: User): Observable<string> {
    this.loader.putMsgLoader('Consultando processamento...');
    const headers = new HttpHeaders({Authorization: user.accessToken});
    return this.http.get<string>(
      `${this.baseUrl}auth/acl/legacyId`,
      {headers, params: {userId: user.id}}
    );
  }

  handleRetorno(ctx: {
    user: User;
    permissions: any[];
    legacyId?: string;
  }): Observable<User> {
    const {user, permissions, legacyId} = ctx;
    const finalUser = {
      ...user,
      permissions,
      legacyUserId: legacyId
    } as User;

    localStorage.setItem('user', JSON.stringify(finalUser));
    localStorage.setItem('group', JSON.stringify(finalUser.contracts && finalUser.contracts?.length > 0 ? finalUser.contracts[0] : null));
    this.currentUserSource.next(finalUser);

    return of(finalUser);
  }

  hasPermission(requiredPermission: any[]): boolean {
    const user = this.getCurrentUser();
    if (!user?.permissions) return false;

    const contract = localStorage.getItem('group');
    const currentContract = contract ? JSON.parse(contract) : undefined;
    const permissionContract: any = user.permissions?.find(
      (p: any) => p.contractId === currentContract?.id
    );
    const allowedPaths = requiredPermission.map(p => p.path);

    return permissionContract?.profiles?.some((profile: any) =>
      profile.profileModuleScreens?.some(
        (pms: any) => allowedPaths.includes(pms.screen?.path)
      )
    );
  }

  canActivateByUserType(allowedUserTypes?: UserTypeEnum[]): boolean {
    if (!allowedUserTypes || allowedUserTypes.length === 0) return false;
    const user = this.getCurrentUser();
    if (!user?.userType) return false;
    return allowedUserTypes.includes(user.userType as UserTypeEnum);
  }

  hasUserType(requiredTypes: string[]): boolean {
    const user = this.getCurrentUser();
    if (!user?.userType) {
      return false;
    }

    const currentType = user.userType;

    return requiredTypes.includes(currentType);
  }

  isLogged(): boolean {
    const jwtToken = this.getToken();
    if (jwtToken) {
      const decodedToken = this.decodeToken(jwtToken);
      return decodedToken?.exp && new Date(decodedToken.exp * 1000).getTime() > new Date().getTime();
    }
    return false;
  }

  private decodeToken(jwtToken: string): any {
    try {
      return jwtDecode(jwtToken);
    } catch (e) {
      console.error(`erro ao decodificar o token ${jwtToken}`)
      return null;
    }
  }

  logout(): void {
    localStorage.clear();
    window.stop(); // ?????
    this.currentUserSource.next(null);
    this.router.navigate(['/login']);
  }

  getToken() {
    const user = this.getCurrentUser();
    return user.accessToken;
  }

  isAuthenticated(): boolean {
    return !!localStorage.getItem('user') && !!localStorage.getItem('group');
  }

  getCurrentUser(): User {
    if (!this.isAuthenticated())
      this.logout();
    if (!this.currentUserSource.value)
      this.currentUserSource.next(JSON.parse(localStorage.getItem('user') ?? '{}'));

    return this.currentUserSource.value;
  }

  changePassword(data: any): Observable<any> {
    const url: string = `${this.baseUrl}auth/updatePassword`;
    return this.http.post(url, data);
  }

  tokenValido(token: string): boolean {
    if (!token)
      return false;

    return !this.jwtHelper.isTokenExpired(token);
  }

  get changeLoggedPass(): boolean {
    return this._changeLoggedPass.value;
  }

  enableChangeLoggedPass() {
    this._changeLoggedPass.next(true);
  }

  disableChangeLoggedPass() {
    this._changeLoggedPass.next(false);
  }

  get changePassSuccess(): boolean {
    return this._changePassSuccess.value;
  }

  enableChangePassSuccess() {
    this._changePassSuccess.next(true);
  }

  disableChangePassSuccess() {
    this._changePassSuccess.next(false);
  }
}
